import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskedLines, Reveal, FadeIn } from "../components/site/Reveal";
import { PrimaryButton, GhostButton, TextLink } from "../components/site/Buttons";
import { ClosingCTA, Marquee } from "../components/site/Shared";
import {
  IMAGES,
  MOMENTS,
  OUTCOMES,
  METHOD,
  CAPABILITIES,
  ENVIRONMENTS,
} from "../data/content";
import { ArrowRight } from "lucide-react";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "12%"]);

  return (
    <section ref={ref} className="relative overflow-hidden">
      {/* Full-bleed portrait image — right side, bleeding up to the header (desktop) */}
      <div className="hidden lg:block absolute top-0 right-0 h-full w-[40vw]">
        <motion.img
          src={IMAGES.heroLibrary}
          alt="A grand reading room — the architecture of knowledge and judgment"
          style={{ y }}
          className="absolute inset-0 w-full h-[114%] object-cover img-treated"
        />
        {/* soft ivory scrim behind the header keeps the nav legible */}
        <div className="absolute inset-x-0 top-0 h-44 bg-gradient-to-b from-ivory to-transparent" />
        {/* left edge melts into the ivory page — wide, feathered transition */}
        <div className="absolute inset-y-0 left-0 w-[26vw] bg-gradient-to-r from-ivory via-ivory/70 to-transparent" />
      </div>

      <div className="relative yadid-container">
        <div className="flex flex-col lg:min-h-[86vh] pt-32 md:pt-40 pb-14 lg:max-w-[62%]">
          <MaskedLines
            tag="h1"
            lines={[
              "Trusted advisory",
              "for leaders navigating",
              "consequential moments.",
            ]}
            delay={0.25}
            className="font-serif text-ink text-[2.4rem] sm:text-5xl lg:text-[3.7rem] xl:text-[4.1rem] leading-[1.05] tracking-[-0.025em]"
          />

          {/* Standfirst + CTAs, grouped together on the left */}
          <div className="mt-12 max-w-xl">
            <Reveal delay={0.7}>
              <p className="text-base md:text-lg text-ink/70 font-light leading-relaxed border-l border-stone pl-5">
                When relationships, reputation, institutions, and long-term impact are at stake,
                leaders need more than advice. They need judgment they can trust.
              </p>
            </Reveal>
            <Reveal delay={0.85}>
              <div className="mt-8 pl-5 flex flex-col sm:flex-row sm:items-center gap-4">
                <PrimaryButton to="/contact" data-testid="hero-cta-primary">
                  Start a Conversation
                </PrimaryButton>
                <GhostButton to="/how-we-advise" data-testid="hero-cta-secondary">
                  How We Advise
                </GhostButton>
              </div>
            </Reveal>
          </div>

          {/* Mobile image */}
          <FadeIn delay={0.4} className="lg:hidden mt-12">
            <div className="relative h-[420px] overflow-hidden">
              <img
                src={IMAGES.heroLibrary}
                alt="A grand reading room — the architecture of knowledge and judgment"
                className="w-full h-full object-cover img-treated"
              />
            </div>
          </FadeIn>

          {/* Understated caption — lower-left gutter */}
          <Reveal delay={1} className="mt-16 lg:mt-auto lg:pt-14">
            <div className="flex items-center gap-5">
              <span className="h-px w-12 bg-brass/70" />
              <span className="eyebrow text-ink/45">Trusted judgment, when it matters most</span>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const Moments = () => (
  <section className="bg-ivory border-t border-stone">
    <div className="yadid-container py-24 md:py-36">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="eyebrow text-brass mb-8">The Moment</p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.08] tracking-[-0.02em] text-ink">
              Some moments require more than expertise.
            </h2>
          </Reveal>
        </div>
        <div className="lg:col-span-6 lg:col-start-7">
          <ol className="divide-y divide-stone">
            {MOMENTS.map((m, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <li className="py-6 flex gap-6 items-baseline">
                  <span className="font-serif text-brass text-lg w-8 shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-lg md:text-xl text-ink/80 font-light leading-relaxed">
                    {m}
                  </span>
                </li>
              </Reveal>
            ))}
          </ol>
          <Reveal delay={0.1}>
            <p className="mt-10 text-ink/60 font-light leading-relaxed max-w-2xl">
              Yadid exists for moments like these. We help leaders understand what is really
              happening, recognize what matters most, navigate the relationships surrounding the
              issue, and move forward with greater clarity and confidence.
            </p>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);

const WhatWeDo = () => (
  <section className="bg-stone-soft/40 border-y border-stone">
    <div className="yadid-container py-24 md:py-36">
      <div className="max-w-4xl">
        <Reveal>
          <p className="eyebrow text-brass mb-8">What Yadid Does</p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-[3.75rem] leading-[1.08] tracking-[-0.02em] text-ink">
            Judgment when the path is not obvious.
          </h2>
        </Reveal>
        <Reveal delay={0.12}>
          <p className="mt-10 text-lg md:text-xl text-ink/70 font-light leading-relaxed">
            Yadid Advisory is a boutique strategic advisory firm for leaders and institutions
            navigating consequential moments involving relationships, reputation, influence,
            partnership, and institutional direction. We bring together strategic counsel,
            relational intelligence, cross-cultural interpretation, and trusted judgment to help
            leaders navigate complexity where the answer is rarely only technical.
          </p>
        </Reveal>
      </div>
    </div>
  </section>
);

const Outcomes = () => (
  <section className="bg-ivory">
    <div className="yadid-container py-24 md:py-36">
      <Reveal>
        <p className="eyebrow text-brass mb-14">The Outcomes</p>
      </Reveal>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-stone">
        {OUTCOMES.map((o, i) => (
          <Reveal key={o.title} delay={i * 0.1} className="bg-ivory">
            <div className="p-8 md:p-10 h-full">
              <span className="font-serif text-brass text-2xl">{String(i + 1).padStart(2, "0")}</span>
              <h3 className="mt-6 font-serif text-3xl md:text-4xl text-ink">{o.title}</h3>
              <p className="mt-5 text-ink/65 font-light leading-relaxed">{o.body}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Method = () => (
  <section className="bg-ink text-ivory">
    <div className="yadid-container py-24 md:py-40">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <Reveal>
            <p className="eyebrow text-brass mb-8">How We Advise</p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-[-0.02em] text-ivory">
              Listen. Reveal. Reimagine. Guide.
            </h2>
            <p className="mt-8 text-ivory/60 font-light leading-relaxed">
              Yadid's advisory philosophy begins with understanding, not prescription.
            </p>
            <div className="mt-10">
              <Link
                to="/how-we-advise"
                data-testid="method-cta"
                className="link-underline eyebrow text-brass inline-flex items-center gap-2"
              >
                Explore How We Advise <ArrowRight size={15} />
              </Link>
            </div>
          </Reveal>
        </div>
        <div className="lg:col-span-7 lg:col-start-6">
          <div className="divide-y divide-white/10 border-t border-white/10">
            {METHOD.map((m, i) => (
              <Reveal key={m.title} delay={i * 0.06}>
                <div className="py-8 flex gap-8 items-baseline">
                  <span className="font-serif text-brass text-xl w-10 shrink-0">{m.n}</span>
                  <div>
                    <h3 className="font-serif text-3xl md:text-4xl text-ivory">{m.title}</h3>
                    <p className="mt-3 text-ivory/60 font-light leading-relaxed max-w-lg">
                      {m.lede}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

const Relationships = () => (
  <section className="bg-ivory">
    <div className="yadid-container py-24 md:py-36">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6">
          <FadeIn>
            <div className="relative h-[340px] md:h-[480px] overflow-hidden">
              <img
                src={IMAGES.conversation}
                alt="Two people in quiet, thoughtful conversation"
                className="w-full h-full object-cover img-treated"
              />
            </div>
          </FadeIn>
        </div>
        <div className="lg:col-span-5 lg:col-start-8">
          <Reveal>
            <p className="eyebrow text-brass mb-8">Relationships</p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-[3.5rem] leading-[1.08] tracking-[-0.02em] text-ink">
              Strategy moves through relationships.
            </h2>
            <p className="mt-8 text-ink/70 font-light leading-relaxed">
              The most sophisticated strategy can fail when trust is absent. Yadid helps leaders
              understand the people and communities surrounding a strategic question, recognize where
              credibility exists and where it must be earned, and build relationships capable of
              carrying meaningful partnership.
            </p>
            <p className="mt-6 text-ink/70 font-light leading-relaxed">
              Our value is not simply who we know. It is how we understand, steward, and responsibly
              activate trust.
            </p>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);

const CapabilitiesSection = () => (
  <section className="bg-stone-soft/40 border-y border-stone">
    <div className="yadid-container py-24 md:py-36">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
        <Reveal>
          <p className="eyebrow text-brass mb-6">How Yadid Helps</p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-[-0.02em] text-ink max-w-2xl">
            Advisory designed around the moment.
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <TextLink to="/capabilities" data-testid="capabilities-cta">
            Explore Our Capabilities <ArrowRight size={15} />
          </TextLink>
        </Reveal>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-stone">
        {CAPABILITIES.map((c, i) => (
          <Reveal key={c.slug} delay={(i % 2) * 0.08} className="bg-ivory">
            <Link
              to="/capabilities"
              className="group block p-8 md:p-12 h-full transition-colors duration-500 hover:bg-stone-soft/50"
            >
              <span className="font-serif text-brass text-xl">{String(i + 1).padStart(2, "0")}</span>
              <h3 className="mt-6 font-serif text-2xl md:text-3xl text-ink leading-tight">
                {c.title}
              </h3>
              <p className="mt-5 text-ink/65 font-light leading-relaxed">{c.teaser}</p>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const WhereWeWork = () => (
  <section className="bg-ivory">
    <div className="yadid-container py-24 md:py-36">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="eyebrow text-brass mb-8">Where We Work</p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-[3.25rem] leading-[1.1] tracking-[-0.02em] text-ink">
              Where strategy, leadership, and relationships intersect.
            </h2>
            <p className="mt-8 text-ink/60 font-light leading-relaxed max-w-md">
              Our work is defined less by sector than by the nature of the moment. Where trust
              matters, relationships carry consequence, and leaders need sound judgment, Yadid can be
              valuable.
            </p>
          </Reveal>
        </div>
        <div className="lg:col-span-6 lg:col-start-7">
          <ul className="border-t border-stone">
            {ENVIRONMENTS.map((e, i) => (
              <Reveal key={e} delay={i * 0.05}>
                <li className="py-6 border-b border-stone flex items-center justify-between group">
                  <span className="font-serif text-2xl md:text-3xl text-ink">{e}</span>
                  <span className="eyebrow text-brass/70">{String(i + 1).padStart(2, "0")}</span>
                </li>
              </Reveal>
            ))}
          </ul>
        </div>
      </div>
    </div>
  </section>
);

const JourneysTeaser = () => (
  <section className="relative bg-ink text-ivory overflow-hidden">
    <div className="absolute inset-0 opacity-30">
      <img src={IMAGES.cloisterLight} alt="" className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-ink/70" />
    </div>
    <div className="relative yadid-container py-28 md:py-44">
      <div className="max-w-3xl">
        <Reveal>
          <p className="eyebrow text-brass mb-8">Another Practice of Yadid</p>
          <h2 className="font-serif text-3xl md:text-5xl lg:text-[3.9rem] leading-[1.06] tracking-[-0.02em] text-ivory">
            Sometimes understanding requires going there.
          </h2>
        </Reveal>
        <Reveal delay={0.12}>
          <p className="mt-4 font-serif text-2xl md:text-3xl text-brass italic">Yadid Journeys</p>
          <p className="mt-8 text-lg md:text-xl text-ivory/70 font-light leading-relaxed max-w-2xl">
            Immersive journeys in history, faith, culture, and relationship. Yadid Journeys brings
            leaders into direct encounter with the places, histories, communities, and relationships
            that shape how people understand the world. These are not conventional tours. They are
            intentionally designed journeys of understanding, encounter, reflection, and
            relationship.
          </p>
        </Reveal>
        <Reveal delay={0.2}>
          <div className="mt-12">
            <Link
              to="/journeys"
              data-testid="journeys-cta"
              className="inline-flex items-center gap-3 border border-brass text-brass px-8 py-4 text-[0.78rem] tracking-[0.16em] uppercase font-semibold transition-all duration-500 hover:bg-brass hover:text-ink"
            >
              Discover Yadid Journeys <ArrowRight size={15} />
            </Link>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <Marquee words={["Trusted Judgment", "Enduring Relationships", "Strategic Impact"]} />
      <Moments />
      <WhatWeDo />
      <Outcomes />
      <Method />
      <Relationships />
      <CapabilitiesSection />
      <WhereWeWork />
      <JourneysTeaser />
      <ClosingCTA />
    </div>
  );
}
